import React from 'react';
import { CalendarDays, TrendingUp, Briefcase, Coins } from 'lucide-react';
import FeaturePage from './FeaturePage';

export default function Seasons() {
  return (
    <FeaturePage
      seo={{
        title: 'Football Manager Season Mode: Promotion & Board | My Football Game',
        description: 'Play a full football manager season online: 18 matchdays, 10 clubs, promotion and relegation across 5 divisions, and a board that judges every result.',
        path: '/features/seasons',
      }}
      eyebrow="Feature Spotlight"
      title="SEASONS & "
      titleAccent="THE BOARD"
      subtitle="A full league season in minutes: 18 matchdays against nine rival clubs, promotion and relegation, and a board that expects results."
      breadcrumbLabel="Seasons"
      heroContent={
        <div className="grid grid-cols-5 gap-3 mb-16 max-w-3xl mx-auto">
          {['Amateur League', 'Regional League', 'National League', 'Championship', 'Elite League'].map((name, i) => (
            <div key={name} className="rounded-lg p-3 text-center" style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}>
              <div className="text-xs font-bold">{name}</div>
              <div className="text-[10px] text-[var(--text-3)]">Division {i + 1}</div>
            </div>
          ))}
        </div>
      }
      sections={[
        {
          icon: <CalendarDays className="w-5 h-5" />,
          title: 'An 18-matchday league season',
          content: (
            <>
              <p>Every club plays in its own 10-club division: your club, your friends' clubs and computer-managed rivals. Each club plays the others home and away, so a season is 18 matchdays. You play each matchday when you want, and every match is simulated live, minute by minute.</p>
              <p>The league table updates after every round, so each result matters: a win against a rival in the promotion race counts twice.</p>
            </>
          ),
        },
        {
          icon: <TrendingUp className="w-5 h-5" />,
          title: 'Promotion and relegation',
          content: (
            <>
              <p>There are five divisions, from the Amateur League up to the Elite League. The top two clubs in a division are promoted at the end of the season and the bottom two are relegated. Higher divisions pay bigger season prizes, but the rival clubs are stronger.</p>
              <p>Finishing first wins the title and the biggest prize in your division. Climbing all the way to the Elite League takes several good seasons in a row.</p>
            </>
          ),
        },
        {
          icon: <Briefcase className="w-5 h-5" />,
          title: 'A board that judges every result',
          content: (
            <>
              <p>Before the season starts, the board predicts where your squad should finish and sets an objective: win the title, get promoted, finish in the top half or avoid relegation. Board confidence starts at 60 and moves after every match. Wins against stronger clubs raise it more, and losses to weaker clubs hurt more.</p>
              <p>If confidence drops below 20, the board gives you an ultimatum: win two of the next three matches. Pass it and the board backs you again. Fail it and you lose the season prize, even if you finish well.</p>
            </>
          ),
        },
        {
          icon: <Coins className="w-5 h-5" />,
          title: 'Season prizes',
          content: (
            <>
              <p>At the end of the season you earn a prize based on your final position and division. Use it on the <a href="/features/transfer-market" style={{ color: 'var(--accent)' }}>transfer market</a> to strengthen the squad before the next season starts.</p>
              <p>Your squad, results and finances carry over from season to season, so the club you build keeps growing.</p>
            </>
          ),
        },
      ]}
      faqItems={[
        { q: 'How long is a season in My Football Game?', a: 'A season has 18 matchdays: each of the 10 clubs in your division plays the other nine twice, once at home and once away. You play each matchday whenever you want, so you can finish a season in one evening or over several days.' },
        { q: 'How do promotion and relegation work?', a: 'The top two clubs in each division are promoted and the bottom two are relegated. There are five divisions, from the Amateur League to the Elite League.' },
        { q: 'Can I get sacked by the board?', a: 'Not sacked, but the board can punish you. If board confidence falls below 20 it gives you an ultimatum: win two of the next three matches. If you fail, you lose that season\'s prize money.' },
        { q: 'Can my friends play in my division?', a: 'Yes. Friends\' clubs fill places in your division, so you play them twice a season and the head-to-head record counts.' },
      ]}
      ctaTitle="Start your first season"
      ctaText="Create your club for free and play matchday one in under a minute."
    />
  );
}
